import { hotKeywords } from "@/mocks/campus";
import Reveal from "./Reveal";
import { scrollToId } from "@/hooks/useRevealObserver";

const stats = [
  { value: "12+", label: "今日上榜话题" },
  { value: "4.6w", label: "累计围观热度" },
  { value: "日更", label: "热榜更新频率" },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden" id="top">
      {/* 装饰色块 */}
      <div className="pointer-events-none absolute -top-20 -right-16 h-72 w-72 rounded-full bg-primary-100/70 blur-2xl" />
      <div className="pointer-events-none absolute top-40 -left-20 h-64 w-64 rounded-full bg-secondary-100/80 blur-2xl" />
      <div className="pointer-events-none absolute bottom-0 right-1/3 h-52 w-52 rounded-full bg-accent-100/70 blur-2xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 md:gap-12 md:px-6 md:py-16 lg:grid-cols-[1.05fr_0.95fr]">
        {/* 左侧文案 */}
        <div className="flex flex-col items-start gap-5 md:gap-6">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3.5 py-1.5 text-xs font-semibold text-primary-700 ring-1 ring-primary-200 whitespace-nowrap">
              <i className="ri-fire-fill text-sm text-primary-500" />
              今日校园热搜正在热播
            </span>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="text-3xl font-bold leading-[1.15] text-foreground-950 md:text-5xl">
              一页看遍，
              <br />
              今天的校园都在<span className="relative whitespace-nowrap text-primary-600">聊什么<i className="absolute -bottom-1.5 left-0 h-1.5 w-full rounded-full bg-accent-300" /></span>
            </h2>
          </Reveal>

          <Reveal delay={160}>
            <p className="max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              校园吃瓜热门话题榜与头条焦点事件全收录，帮你把散落在表白墙、社团群和食堂里的热议，整理成一份清爽的今日热搜清单。围观要趁热，理性不掉队。
            </p>
          </Reveal>

          <Reveal delay={220}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => scrollToId("hot")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-fire-line text-lg" />
                围观今日热榜
              </button>
              <button
                type="button"
                onClick={() => scrollToId("faq")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line text-lg" />
                先看吃瓜指南
              </button>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <dl className="mt-2 flex w-full flex-wrap gap-x-8 gap-y-4 border-t border-background-200/80 pt-5">
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col gap-0.5">
                  <dt className="order-2 text-xs text-foreground-500">{s.label}</dt>
                  <dd className="font-heading text-2xl text-primary-600 md:text-3xl">{s.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>

        {/* 右侧主视觉 */}
        <Reveal delay={160} className="relative">
          <div className="relative overflow-hidden rounded-[2rem] ring-1 ring-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Cheerful%20campus%20lifestyle%20flat%20illustration%20of%20a%20lively%20school%20lawn%20with%20banners%20confetti%20a%20bulletin%20board%20and%20speech%20bubbles%20of%20hot%20campus%20topics%20floating%20in%20the%20air%2C%20warm%20sunshine%20feeling%2C%20editorial%20vector%20style%20in%20watermelon%20red%20leaf%20green%20and%20golden%20yellow%20palette%2C%20cream%20textured%20background%2C%20clean%20rounded%20shapes%2C%20no%20people%20faces%2C%20high%20detail%20harmonious%20composition&width=900&height=760&seq=gua-hero-01&orientation=portrait"
              alt="校园吃瓜热门话题榜首屏插画"
              title="校园瓜田 校园吃瓜热门话题榜"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background-950/25 to-transparent" />
          </div>

          {/* 浮动置顶卡 */}
          <div className="absolute -left-3 top-6 hidden items-center gap-2 rounded-2xl bg-background-50 px-4 py-3 shadow-soft ring-1 ring-background-200 md:flex animate-floaty">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-100 text-primary-700"><i className="ri-fire-fill text-base" /></span>
            <span className="flex flex-col leading-tight">
              <span className="text-xs font-bold text-foreground-950">热度 TOP1</span>
              <span className="text-[11px] text-foreground-500">占座大战进行中</span>
            </span>
          </div>

          <div className="absolute -bottom-4 -right-2 hidden items-center gap-2 rounded-2xl bg-secondary-100 px-4 py-3 ring-1 ring-secondary-200 md:flex">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-secondary-200 text-secondary-800"><i className="ri-eye-line text-base" /></span>
            <span className="flex flex-col leading-tight">
              <span className="text-xs font-bold text-foreground-950">实时围观中</span>
              <span className="text-[11px] text-foreground-600">4.6w 名同学在线</span>
            </span>
          </div>
        </Reveal>
      </div>

      {/* 今日热议关键词 */}
      <div className="relative mx-auto max-w-7xl px-4 pb-10 md:px-6">
        <Reveal delay={120}>
          <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-background-100/80 p-3.5 ring-1 ring-background-200">
            <span className="mr-1 inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800 whitespace-nowrap">
              <i className="ri-hashtag text-sm" />
              今日热议
            </span>
            {hotKeywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => scrollToId("hot")}
                className="rounded-full bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-700 ring-1 ring-background-300 transition-colors hover:bg-primary-100 hover:text-primary-800 hover:ring-primary-200 whitespace-nowrap cursor-pointer"
              >
                {kw}
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}